import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react'

import {
  companyFullLogoUrl,
  companyLogoUrl,
  directoryLogoUrl,
  subscribeCompanyLogos,
  SYMBOL_LOGOS,
} from '@/lib/company-logos'
import { cn } from '@/lib/utils'

/**
 * The mark's box, in px, on the 4-grid. V2 settles the logo at 32 in a table's
 * symbol cell and 24 in a dense one (`4236:69879`, the 2026-09-28 design
 * refresh), which are `sm` (the default) and `xs`. The rest are the sizes the
 * app drew before the refresh, snapped to the grid: `md` 38 → 40 (MoverRow's
 * card mark, the fund tables), `lg` 40 (StockHeader) and `xl` 48 (headroom).
 */
const PRESET_PX = { xs: 24, sm: 32, md: 40, lg: 40, xl: 48 } as const

/**
 * One literal class per box, so Tailwind's build-time scan sees each of them —
 * a `size-${n}` built at runtime would never be generated. The letter steps up
 * a size in the header-scale boxes, where a 12px letter reads as a speck.
 */
const BOXES = {
  24: { box: 'size-6', text: 'text-body-xs' },
  28: { box: 'size-7', text: 'text-body-xs' },
  32: { box: 'size-8', text: 'text-body-xs' },
  36: { box: 'size-9', text: 'text-body-xs' },
  40: { box: 'size-10', text: 'text-label-l' },
  44: { box: 'size-11', text: 'text-label-l' },
  48: { box: 'size-12', text: 'text-label-l' },
} as const

type BoxPx = keyof typeof BOXES

export type StockAvatarSize = keyof typeof PRESET_PX | number

/**
 * A raw px size — the Figma measurements a few callers still pass (Spotlight's
 * 36, AlertForm's 28) — snapped to the nearest multiple of 4, a tie going to
 * the multiple of 8 (26 → 24, 30 → 32, 38 → 40), and held inside the boxes
 * `BOXES` has classes for.
 */
const boxPxFor = (size: StockAvatarSize): BoxPx => {
  if (typeof size !== 'number') return PRESET_PX[size]
  const below = Math.floor(size / 4) * 4
  const above = below + 4
  const snapped =
    size - below === above - size
      ? below % 8 === 0
        ? below
        : above
      : size - below < above - size
        ? below
        : above
  return Math.min(48, Math.max(24, snapped)) as BoxPx
}

/** How long a failed URL stays failed before it's worth trying again. */
const FAILURE_TTL_MS = 5 * 60_000

/**
 * URLs that have already failed to load, this session, mapped to when. About
 * one symbol in five has no `/media/` logo on the CDN (58 of `/market/live/`'s
 * 279, measured 2026-11-01), so without this, every mount of a 200-row table
 * re-fires requests that are already known to fail. Module-level, bounded by the number of distinct URLs
 * ever seen (never by row count), and read through `isFailed` rather than a
 * raw `.has()` — a failure older than `FAILURE_TTL_MS` is evicted on that
 * read, so a URL that only had a network blip stops being penalised, while a
 * URL that keeps failing gets re-marked and stays out for another window.
 * This also means a new CDN file or a backend fix shows up without a reload:
 * once the entry expires (or was never added), the next render tries the URL
 * again on its own.
 */
const failedUrls = new Map<string, number>()

const isFailed = (url: string): boolean => {
  const failedAt = failedUrls.get(url)
  if (failedAt === undefined) return false
  if (Date.now() - failedAt < FAILURE_TTL_MS) return true
  failedUrls.delete(url)
  return false
}

const markFailed = (url: string): void => {
  failedUrls.set(url, Date.now())
}

/**
 * URLs a probe has fully loaded this session. The file is in the browser's
 * cache by then, so an `<img>` pointed at one paints straight away.
 */
const loadedUrls = new Set<string>()

/** One probe per URL in flight, however many avatars draw that URL. */
const probesInFlight = new Set<string>()

/** The avatars waiting on each URL's probe. */
const probeListeners = new Map<string, Set<() => void>>()

/**
 * Loads `url` off-DOM, once for the whole app, and tells every avatar
 * waiting on it when it settles.
 *
 * ── Why a shared `Image` and not a hidden `<img>` per avatar ──────────────
 *
 * Each avatar used to stack its own `<img loading="lazy" class="opacity-0">`
 * probe behind the fallback, with an `onError` that set state. Every logo URL
 * then 404'd, so on a 100-row Movers table each probe started as its row
 * neared the viewport, failed, and re-rendered that row. Measured 2026-09-25
 * in a production build: one scroll down and back up fired 62 requests and
 * 17 React commits mid-scroll. Now the probe starts when the avatar mounts,
 * not when it scrolls into view, so that work lands with the table rather
 * than under the member's finger; two avatars with the same URL (a Movers row
 * and the rail's Popular stocks) share one request; and a failure changes
 * nothing an avatar reads — it was already drawing its fallback — so it costs
 * no render at all. Only a load re-renders, to swap the logo in. A failure
 * moves an avatar on to its next remote URL from the probe's own callback
 * (`probeNextCandidate`), not from a render, so that step is free too.
 */
const startProbe = (url: string): void => {
  if (loadedUrls.has(url) || isFailed(url) || probesInFlight.has(url)) return
  probesInFlight.add(url)

  const settle = () => {
    probesInFlight.delete(url)
    for (const listener of probeListeners.get(url) ?? []) listener()
  }
  const probe = new Image()
  probe.decoding = 'async'
  probe.onload = () => {
    loadedUrls.add(url)
    settle()
  }
  probe.onerror = () => {
    markFailed(url)
    settle()
  }
  probe.src = url
}

const subscribeToProbe = (url: string, onSettle: () => void): (() => void) => {
  const listeners = probeListeners.get(url) ?? new Set<() => void>()
  probeListeners.set(url, listeners)
  listeners.add(onSettle)
  return () => {
    listeners.delete(onSettle)
    if (listeners.size === 0 && probeListeners.get(url) === listeners) probeListeners.delete(url)
  }
}

/** The first of `urls` still worth trying — never failed, or failed long enough ago. */
const firstUnfailed = (urls: readonly string[]): string | undefined =>
  urls.find((url) => !isFailed(url))

const probeNextCandidate = (urls: readonly string[]): void => {
  const next = firstUnfailed(urls)
  if (next !== undefined) startProbe(next)
}

/**
 * The first of `candidates` still worth trying, once a probe has proven it
 * loads; `undefined` while it is loading, or when every candidate failed.
 *
 * Strictly in order: a later candidate that happens to be loaded already (by
 * another avatar) waits behind an earlier one still in flight, so every
 * avatar for a symbol lands on the same image.
 *
 * Each settle re-runs `probeNextCandidate` for every avatar subscribed to
 * that URL, not just the one whose render started the probe: two avatars can
 * share a CDN probe while holding different chains (one with a row
 * `logo_url`, one without), and the one that did not start it must still move
 * on to its own next URL when it fails.
 */
const useFirstLoadedLogo = (candidates: readonly string[]): string | undefined => {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const unsubscribes = candidates.map((url) =>
        subscribeToProbe(url, () => {
          probeNextCandidate(candidates)
          onChange()
        }),
      )
      return () => {
        for (const unsubscribe of unsubscribes) unsubscribe()
      }
    },
    [candidates],
  )

  // After EVERY render, not once per chain: a failure no longer re-renders the
  // avatar (see `startProbe`), so the subscription outlives it, and a
  // subscription keyed on `candidates` alone would never ask again once the
  // failure's TTL ran out. This keeps `failedUrls`' contract — the next
  // render after it expires tries the URL on its own. A no-op while a probe
  // is in flight or the URL is loaded or still failed, so it costs a few
  // `Set`/`Map` reads per render.
  useEffect(() => {
    probeNextCandidate(candidates)
  })

  return useSyncExternalStore(subscribe, () => {
    const current = firstUnfailed(candidates)
    return current !== undefined && loadedUrls.has(current) ? current : undefined
  })
}

const isAbsoluteUrl = (url: string | null | undefined): url is string =>
  typeof url === 'string' && /^https?:\/\//i.test(url)

/**
 * The remote URLs to try, in order, each once: the `/media/` logo built from
 * `symbol`, the row's own `logoUrl`, the symbol's `logo_url` from the
 * directory `/market/live/` fills, then the `/logo_full/` file built from
 * `symbol`.
 *
 * The `/media/` URL stays first even though `logo_url` now hits more often
 * (271 of 279 against 221): every avatar can build it from the symbol alone,
 * so two rows for one company, one sending `logo_url` and one not, settle on
 * the same image, and an avatar showing it does not flip to another file when
 * the directory arrives after the first paint.
 *
 * A blank `symbol` (`AlertForm` before a company is picked, an issue with no
 * ticker yet) builds no CDN URL: `/media/.png` is not a logo. A relative
 * `logoUrl` is skipped rather than resolved — a stock logo is never one, and
 * a bare `<img src>` would resolve it against the app's own origin.
 */
const remoteCandidates = (
  symbol: string,
  logoUrl: string | null | undefined,
  directoryUrl: string | undefined,
): string[] => {
  const hasSymbol = symbol.trim() !== ''
  const urls = [
    hasSymbol ? companyLogoUrl(symbol) : null,
    logoUrl,
    directoryUrl,
    hasSymbol ? companyFullLogoUrl(symbol) : null,
  ].filter(isAbsoluteUrl)
  return [...new Set(urls)]
}

interface StockAvatarProps {
  /** The NEPSE ticker. Builds the CDN logo URL, keys the bundled PNG, and is
   *  the letter's source unless `monogram` says otherwise. Blank for a row
   *  with no ticker, which then skips straight to its fallbacks. */
  symbol: string
  /** The company name. Feeds the accessible label when `standalone`; the
   *  letter mark itself reads off `symbol` (or `monogram`), matching every
   *  existing mark in the app (`SymbolCell`, `StockMark`, `PopularStocksCard`,
   *  `MoverRow`). */
  name?: string
  /** The row's own `logo_url`, when its endpoint sends one — tried after the
   *  CDN logo built from `symbol`, and only when it is a different URL.
   *  `string | null` so a field like `WatchRow.logo` passes straight through
   *  with no omit-the-key workaround — unlike `SymbolCell.logo`, which is
   *  `string | undefined` and forces every caller with a nullable field into
   *  a `logo === null ? {} : { logo }` spread just to satisfy
   *  `exactOptionalPropertyTypes`. */
  logoUrl?: string | null
  /** What the letter tile spells when no image loads. Defaults to the
   *  symbol's first letter; `FundCell` passes a fund's two letters, and a row
   *  with no ticker (an unlisted issue, a market-wide note) passes its own
   *  name's initial alongside a blank `symbol`. */
  monogram?: string
  size?: StockAvatarSize
  shape?: 'circle' | 'square'
  /** Decorative by default (`alt=""`, an `aria-hidden` letter) for the common
   *  case — a symbol or company name is printed right beside the mark, and
   *  naming the mark too would read every row twice. Set when the mark
   *  stands with no label of its own beside it, to get `name ?? symbol` as a
   *  real accessible name instead. */
  standalone?: boolean
  className?: string
}

/**
 * One shared mark for a stock or company: the CDN logo built from `symbol`
 * (`companyLogoUrl`) → the row's own `logoUrl` → the symbol's `logo_url` from
 * the live-market directory → the `/logo_full/` file (`remoteCandidates`) →
 * the bundled PNG in `company-logos.ts` → a letter mark. Each step is taken on the previous
 * one's load error, so a symbol alone is enough for a logo on every surface,
 * whether or not its endpoint sends a `logo_url`.
 *
 * A logo sits on a white plate (`bg-logo-plate`, V2's `logo-plate`,
 * `4236:69879`) in both themes, because most logos are dark ink on a
 * transparent ground drawn for white. The letter sits on the muted disc
 * instead, in muted ink, so a mark with no logo reads as a placeholder rather
 * than as a white logo that failed to draw.
 *
 * The box size never changes across steps — one size class fixes both
 * dimensions up front, and every `<img>` here carries the same `width`/`height`
 * attributes — so a logo landing late, or an image failing over to the letter,
 * shifts nothing else in the row.
 *
 * The remote chain and the bundled fallback are read fresh on every render,
 * straight off `isFailed` and the shared probe state — nothing about which
 * step to show is cached in this component's own state. That's what makes
 * the chain land on the same answer in every avatar that draws a URL: they
 * all read the one `failedUrls` map and the one `loadedUrls` set, rather than
 * one of them trusting a step it decided on an earlier render.
 *
 * ── Why the remote image never shows a blank frame ─────────────────────────
 *
 * Rendering `<img src={cdnUrl}>` straight away would paint nothing (or a
 * browser's broken-image glyph) for as long as that request takes, then
 * again for the row's own URL if the CDN has no file — a
 * letter-then-blank-then-logo flicker on every first view. Instead, each
 * remote candidate loads in the background through `startProbe`, and only
 * becomes the visible mark once that probe has loaded it; until then the
 * bundled PNG or the letter holds the slot. The probes are keyed on the
 * CURRENT chain, so this self-heals when `symbol` or `logoUrl` changes
 * without this instance remounting: the new URL is not loaded yet, so the
 * fallback shows again while it loads.
 */
export default function StockAvatar({
  symbol,
  name,
  logoUrl,
  monogram,
  size = 'sm',
  shape = 'circle',
  standalone = false,
  className,
}: StockAvatarProps) {
  const directoryUrl = useSyncExternalStore(subscribeCompanyLogos, () => directoryLogoUrl(symbol))
  // Memoised on the three strings it reads, so the probe subscription below is
  // not torn down and rebuilt on every render of a 200-row table.
  const candidates = useMemo(
    () => remoteCandidates(symbol, logoUrl, directoryUrl),
    [symbol, logoUrl, directoryUrl],
  )
  const remoteUrl = useFirstLoadedLogo(candidates)
  const bundledUrl = SYMBOL_LOGOS[symbol.trim().toUpperCase()]

  // `failedUrls` is a plain `Map`, so recording a failure in it doesn't tell
  // React this component has anything new to paint — this is that nudge.
  // Its own value is never read, only bumped, so the bundled image's onError
  // can force the next render to re-check `isFailed` instead of leaving a
  // just-broken image on screen until something unrelated re-renders it.
  const [, bumpAfterFailure] = useState(0)

  const bundledUsable = bundledUrl !== undefined && !isFailed(bundledUrl)

  const boxPx = boxPxFor(size)
  const letter = monogram?.trim() || symbol.trim().slice(0, 1) || '?'
  const accessibleLabel = name ?? symbol
  const imageUrl = remoteUrl ?? (bundledUsable ? bundledUrl : undefined)

  return (
    <span
      className={cn(
        // `relative` holds the standalone label's `sr-only` span, which is
        // absolutely positioned, inside the mark.
        'relative inline-flex shrink-0 items-center justify-center overflow-hidden',
        BOXES[boxPx].box,
        shape === 'circle' ? 'rounded-full' : null,
        // `p-1` keeps a logo's edge off the circle's clip. The hairline ring is
        // for light, where the white plate meets a white card; on the dark card
        // #1c1c1c against #111 draws nothing a reader can see.
        imageUrl === undefined
          ? 'bg-muted text-muted-foreground'
          : 'bg-logo-plate p-1 ring-1 ring-border-subtle',
        className,
      )}
    >
      {imageUrl === undefined ? (
        <>
          <span aria-hidden="true" className={cn('font-semibold', BOXES[boxPx].text)}>
            {letter}
          </span>
          {standalone ? <span className="sr-only">{accessibleLabel}</span> : null}
        </>
      ) : (
        <img
          src={imageUrl}
          alt={standalone ? accessibleLabel : ''}
          width={boxPx}
          height={boxPx}
          loading="lazy"
          decoding="async"
          className="size-full object-contain"
          // Only the bundled PNG is shown before a probe has proven it loads,
          // so it is the only image that can still fail on screen.
          onError={
            remoteUrl === undefined && bundledUrl !== undefined
              ? () => {
                  markFailed(bundledUrl)
                  bumpAfterFailure((tick) => tick + 1)
                }
              : undefined
          }
        />
      )}
    </span>
  )
}
